import moment from "moment";
import qs from "qs";
import { useCallback, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useRecoilValue } from "recoil";
import { BuildingModel } from "src/api/building/building-types";
import { FacilityUsageSlotSearchTimeType } from "src/api/contract/facility-usage-slot-types";
import { Order } from "src/api/public-types";
import { BaseInput, BaseInputWithSearch, BaseSelect, BaseTable } from "src/components";
import { MultiSelectFilter } from "src/components/multiSelect";
import ContractSelectModal from "src/components/contract/ContractSelectModal";
import RangeDatepickerWithSelect from "src/components/RangeDatepickerWithSelect";
import SearchResetButton from "src/components/SearchResetButton";
import useNavigate from "src/hooks/usePartnerNavigate";
import ProductBuildingListModal from "src/pages/product/components/ProductBuildingListModal";
import { reservationStatuses } from "src/pages/reservation/reservation-types";
import { globalPartnerState } from "src/recoil/partners/atom";
import { YmdFormat } from "src/utils/common-util";
import { facilityUsageStatusOptions, settlementSearchTimeTypeOptions, settlementSearchTypeOptions } from "../commonSpaceSettlement-types";
import useCommonSpaceSettlement, { SettlementQueryParams } from "../hooks/useCommonSpaceSettlement";
import { commonSpaceSettlementColumns, commonSpaceSettlementDisabledSortHeaders } from "./columns/CommonSpaceSettlementColumns";

// 조회 파라미터(YYYY-MM-DD) → 기간 선택값
const toDateRange = (params: SettlementQueryParams): [Date | null, Date | null] => [
  params.searchStartTime ? moment(params.searchStartTime).toDate() : null,
  params.searchEndTime ? moment(params.searchEndTime).toDate() : null,
];

/**
 * 공용공간 정산 > 목록 화면 (RCL31)
 */
const CommonSpaceSettlementList = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const partner = useRecoilValue(globalPartnerState);

  const { settlementQueryParam, settlementList, pageMeta, onDownloadExcel } = useCommonSpaceSettlement();

  const [params, setParams] = useState<SettlementQueryParams>({ ...settlementQueryParam });
  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>(toDateRange(settlementQueryParam));

  // 건물/계약 선택 모달
  const [buildingModalOpen, setBuildingModalOpen] = useState(false);
  const [tempBuilding, setTempBuilding] = useState<BuildingModel | null>(null);
  const [contractModalOpen, setContractModalOpen] = useState(false);

  useEffect(() => {
    setParams(settlementQueryParam);
    setDateRange(toDateRange(settlementQueryParam));
  }, [settlementQueryParam]);

  const navigateWithQueryParams = useCallback(
    (passParams?: SettlementQueryParams) => {
      const newQueryParams = { ...params, ...(passParams || {}) };
      const newQueryParamStr = qs.stringify(newQueryParams, { allowDots: true });
      navigate(location.pathname + "?" + decodeURIComponent(newQueryParamStr));
    },
    [navigate, location.pathname, params],
  );

  // 기간 해제: 빈 값으로 전달해 당월 기본값 적용을 막고 전체 기간 조회
  const clearDateRange = () => {
    navigateWithQueryParams({ searchStartTime: "", searchEndTime: "", page: 0 });
  };

  // 기간 선택: 시작/종료 모두 선택 시 조회
  const handleDateRangeChange = (range: [Date | null, Date | null]) => {
    setDateRange(range);
    const [start, end] = range;
    if (!start && !end) {
      clearDateRange();
      return;
    }
    if (start && end) {
      navigateWithQueryParams({
        searchStartTime: moment(start).format(YmdFormat.YYYY_MM_DD),
        searchEndTime: moment(end).format(YmdFormat.YYYY_MM_DD),
        page: 0,
      });
    }
  };

  // 정산 상태 멀티 셀렉트 선택값
  const selectedUsageStatuses: string[] = params.usageStatus ? params.usageStatus.split(",") : [];

  return (
    <div className="page-product-list">
      <div className="contents-container__table">
        <div className="contents-container__search-wrap">
          <div className="left-area">
            {/* 지정검색: 건물 / 계약 선택 */}
            <section>
              <div className="left-area__index">
                <span>지정검색</span>
              </div>
              <div className="left-area__contents">
                {/* 건물선택 */}
                <div className="minmax160">
                  <BaseInput
                    type="text"
                    readonly={true}
                    placeholder="건물선택"
                    value={params.buildingName || ""}
                    onClick={(e: any) => {
                      e.preventDefault();
                      setTempBuilding(null);
                      setBuildingModalOpen(true);
                    }}
                    onClearClick={() => {
                      setParams({ ...params, buildingId: undefined, buildingName: undefined });
                      navigateWithQueryParams({ ...params, buildingId: undefined, buildingName: undefined, page: 0 });
                    }}
                  />
                </div>

                {/* 계약선택 */}
                <div className="minmax160">
                  <BaseInput
                    type="text"
                    readonly={true}
                    placeholder="계약선택"
                    value={params.contractManageId || ""}
                    onClick={(e: any) => {
                      e.preventDefault();
                      setContractModalOpen(true);
                    }}
                    onClearClick={() => {
                      setParams({ ...params, contractId: undefined, contractApplyNumber: undefined, contractManageId: undefined });
                      navigateWithQueryParams({
                        ...params,
                        contractId: undefined,
                        contractApplyNumber: undefined,
                        contractManageId: undefined,
                        page: 0,
                      });
                    }}
                  />
                </div>
              </div>
            </section>

            {/* 조건검색: 기간 / 예약 상태 / 정산 상태 / 텍스트 */}
            <section>
              <div className="left-area__index">
                <span>조건검색</span>
              </div>
              <div className="left-area__contents">
                {/* 기간옵션 + 기간선택 (일 단위) */}
                <div>
                  <RangeDatepickerWithSelect
                    stateOptions={settlementSearchTimeTypeOptions}
                    selectValue={params.searchTimeType}
                    setStateValue={(searchTimeType: FacilityUsageSlotSearchTimeType) => {
                      setParams({ ...params, searchTimeType });
                      // 기간이 선택된 경우에만 조회 (미선택 시 전 기간 조회라 결과 동일)
                      if (params.searchStartTime && params.searchEndTime) {
                        navigateWithQueryParams({ searchTimeType, page: 0 });
                      }
                    }}
                    dateRange={dateRange}
                    onChange={handleDateRangeChange}
                    onClearClick={clearDateRange}
                  />
                </div>

                {/* 예약 상태 */}
                <div className="minmax160">
                  <BaseSelect
                    placeholder="예약 상태"
                    value={params.reservationStatus}
                    stateOptions={reservationStatuses}
                    setStateValue={(reservationStatus: string) => {
                      setParams({ ...params, reservationStatus });
                      navigateWithQueryParams({ reservationStatus, page: 0 });
                    }}
                  />
                </div>

                {/* 정산 상태 (복수) */}
                <div className="minmax160">
                  <MultiSelectFilter
                    placeholder="정산 상태"
                    options={facilityUsageStatusOptions}
                    value={selectedUsageStatuses}
                    onChange={(values) => {
                      const usageStatus = values.join(",");
                      setParams({ ...params, usageStatus });
                      navigateWithQueryParams({ ...params, usageStatus, page: 0 });
                    }}
                  />
                </div>

                {/* 텍스트 검색 */}
                <BaseInputWithSearch
                  type="text"
                  selectValue={params.searchType}
                  inputValue={params.searchValue}
                  stateOptions={settlementSearchTypeOptions}
                  setStateValue={(searchType: string) => {
                    setParams({ ...params, searchType: searchType as SettlementQueryParams["searchType"] });
                  }}
                  onChange={(searchValue: string) => {
                    setParams({ ...params, searchValue });
                  }}
                  onClearClick={() => {
                    setParams({ ...params, searchType: undefined, searchValue: "" });
                    navigateWithQueryParams({ ...params, searchType: undefined, searchValue: "", page: 0 });
                  }}
                  onKeyUp={() => navigateWithQueryParams({ page: 0 })}
                  onSearchClick={() => navigateWithQueryParams({ page: 0 })}
                />
                <SearchResetButton />
              </div>
            </section>
          </div>
        </div>

        {settlementList && (
          <BaseTable
            data={settlementList}
            columns={commonSpaceSettlementColumns}
            pageIndex={pageMeta?.pageRequest.page || 0}
            totalPages={pageMeta?.totalPages || 0}
            totalElements={Number(pageMeta?.totalElements) || 0}
            currentSize={Number(params.size) || 20}
            goPage={(page: number) => navigateWithQueryParams({ page })}
            sizeOption={(size: number) => {
              setParams({ ...params, size });
              navigateWithQueryParams({ ...params, page: 0, size });
            }}
            orders={params.sort?.orders}
            disabledSortHeaders={commonSpaceSettlementDisabledSortHeaders}
            setOrders={(orders?: Array<Order>) => {
              if (orders) {
                navigateWithQueryParams({ sort: { orders }, page: 0 });
              }
            }}
            children={
              <button
                className="base-btn color-white icon-excel"
                onClick={() => {
                  // 미적용 입력값(검색어 등)이 섞이지 않도록 현재 목록 조회 조건으로 추출
                  onDownloadExcel(settlementQueryParam);
                }}
              >
                공용공간 정산 내역 엑셀받기
              </button>
            }
          />
        )}
      </div>

      {/* 건물 선택 모달 */}
      {buildingModalOpen && (
        <ProductBuildingListModal
          partnerId={String(partner?.id)}
          setSelectedBuilding={(building: BuildingModel) => setTempBuilding(building)}
          onClose={() => setBuildingModalOpen(false)}
          onConfirm={() => {
            if (tempBuilding?.id) {
              const buildingId = Number(tempBuilding.id);
              const buildingName = tempBuilding.buildingName || "";
              setParams({ ...params, buildingId, buildingName });
              navigateWithQueryParams({ ...params, buildingId, buildingName, page: 0 });
            }
            setBuildingModalOpen(false);
          }}
        />
      )}

      {/* 계약 선택 모달 */}
      {contractModalOpen && (
        <ContractSelectModal
          defaultCheckedContractList={
            params.contractId
              ? [{ contractId: Number(params.contractId), contractApplyNumber: params.contractApplyNumber || "" }]
              : []
          }
          onCanceled={() => setContractModalOpen(false)}
          onAdded={(contractList) => {
            const selected = contractList?.[0];
            if (selected?.contractId) {
              const contractId = Number(selected.contractId);
              const contractApplyNumber = selected.contractApplyNumber || "";
              const contractManageId = selected.contractManageId ? String(selected.contractManageId) : "";
              setParams({ ...params, contractId, contractApplyNumber, contractManageId });
              navigateWithQueryParams({ ...params, contractId, contractApplyNumber, contractManageId, page: 0 });
            }
            setContractModalOpen(false);
          }}
        />
      )}
    </div>
  );
};

export default CommonSpaceSettlementList;
